import type { Lang } from './i18n.ts';

/**
 * 公开页面按网址分语言（2026-11-04）：`/features` 是简体，`/zh-hk/features` 是香港繁体，`/en/features` 是英文。
 *
 * ════════════════════════════════════════════════════════
 * 为什么要按网址分
 * ════════════════════════════════════════════════════════
 * 界面一直能切三种语言，但切换只记在浏览器里，网址只有一份 —— 搜索引擎和 AI 爬虫读到的永远是简体。
 * 用英文搜「memecoin trading discipline tool」的人、用英文问 ChatGPT 的人，就找不到这个网站。
 * 每种语言有自己的网址，再用 hreflang 告诉搜索引擎「这三份是同一页的不同语言」，它才会按搜索语言给对的那份。
 *
 * ── 规矩 ──
 *   只有下面 LOCALIZED 这几页有三种网址。使用教程只有简体（正文没翻译），应用页面（/p/…）语言跟着账号设置走。
 *   **在这几页上，网址说了算**：打开 /en/features 就是英文，打开 /features 就是简体
 *   （index.html 里那段内联脚本在 React 起来之前就把语言写好，避免先画一遍别的语言）。
 *   在这几页上点「简 / 繁 / EN」会换到对应的网址（LandingShell 的 LangSwitch）。
 *   index.html 里抄了一份 LOCALIZED 和前缀表 —— 有测试盯着两边一致。
 */

export interface Locale {
  /** 网址前缀。简体没有前缀 */
  prefix: '' | '/zh-hk' | '/en';
  lang: Lang;
  /** hreflang 写法 */
  hreflang: 'zh-CN' | 'zh-HK' | 'en';
  /** <html lang> 写法，和 index.html 内联脚本里的换算一致 */
  htmlLang: 'zh-Hans' | 'zh-Hant-HK' | 'en';
  ogLocale: 'zh_CN' | 'zh_HK' | 'en_US';
}

export const LOCALES: readonly Locale[] = [
  { prefix: '', lang: 'zh-CN', hreflang: 'zh-CN', htmlLang: 'zh-Hans', ogLocale: 'zh_CN' },
  { prefix: '/zh-hk', lang: 'zh-HK', hreflang: 'zh-HK', htmlLang: 'zh-Hant-HK', ogLocale: 'zh_HK' },
  { prefix: '/en', lang: 'en', hreflang: 'en', htmlLang: 'en', ogLocale: 'en_US' }
];

/** 有三种语言网址的页面（不带前缀的写法） */
export const LOCALIZED: readonly string[] = ['/', '/features', '/video', '/paper', '/privacy', '/gmgn-key', '/partners'];

/** 把一个路径拆成「哪种语言 + 去掉前缀之后的路径」。不是带前缀的路径就当简体 */
export function splitLocale(pathname: string): { locale: Locale; base: string } {
  const p = pathname.replace(/\/+$/, '') || '/';
  for (const l of LOCALES) {
    if (l.prefix === '') continue;
    if (p === l.prefix) return { locale: l, base: '/' };
    if (p.startsWith(l.prefix + '/')) return { locale: l, base: p.slice(l.prefix.length) };
  }
  return { locale: LOCALES[0] as Locale, base: p };
}

export function localeOf(lang: Lang): Locale {
  return LOCALES.find(l => l.lang === lang) ?? (LOCALES[0] as Locale);
}

/** 这个路径（不带前缀）是不是分语言的那几页 */
export function isLocalized(base: string): boolean {
  return LOCALIZED.includes(base);
}

/**
 * 使用教程只有简体和英文两份（2026-11-04 翻了英文，lib/docs-content-en.ts）：`/docs/…` 和 `/en/docs/…`。
 * 没有繁体那一份 —— 繁体网址上的教程链接指向简体
 */
export function isDocs(base: string): boolean {
  return base === '/docs' || base.startsWith('/docs/');
}

/** 这一页（不带前缀）有没有英文网址：分语言那几页 + 使用教程 */
export function hasEnglish(base: string): boolean {
  return isLocalized(base) || isDocs(base);
}

/**
 * 站内链接换成某种语言的网址。只换 LOCALIZED 那几页和使用教程（教程只换英文），别的（/p/…、/login?…）原样返回。
 * 保留 `#锚点` 和 `?参数`：`/#price` → `/en#price`
 */
export function localize(to: string, prefix: Locale['prefix']): string {
  const m = /^([^?#]*)(.*)$/.exec(to);
  const path = m?.[1] ?? to;
  const rest = m?.[2] ?? '';
  const base = path === '' ? '/' : path;
  if (isDocs(base)) return (prefix === '/en' ? '/en' + base : base) + rest;
  if (!isLocalized(base)) return to;
  if (prefix === '') return base + rest;
  return (base === '/' ? prefix : prefix + base) + rest;
}
